// frontend/src/components/Sidebar.js
import React, { useState } from 'react';

function Sidebar({ sampleQueries, onSampleQueryClick }) {
  const [activeCategory, setActiveCategory] = useState('all');

  // Categorized sample queries
  const categories = {
    viewership: [
      "Which content has the highest viewership across all platforms?",
      "How many viewers watched Dil Se Dua on Samsung TV Plus?",
      "Compare viewership between drama and comedy content"
    ],
    demographics: [
      "What is the demographic breakdown of viewers for drama series on Samsung TV Plus?",
      "Which age group watches the most comedy content?",
      "Gender distribution of viewers for action movies"
    ],
    qoe: [
      "Which regions experience the most buffering issues, and on which platforms?",
      "Compare startup times across different platforms",
      "What's the playback error rate on Mi TV+?"
    ],
    conversion: [
      "What's the conversion rate from viewing to app installs for our top 5 shows?",
      "Which content drives the most subscriptions?",
      "Compare app install rates by platform"
    ],
    performance: [
      "How does the viewership of comedy content compare across different age groups?",
      "Which content types have the highest average completion rates?",
      "Compare audience retention for series vs. movies"
    ]
  };

  // Extract all queries for the 'all' category
  const allQueries = Object.values(categories).flat();

  // Function to get queries based on active category
  const getActiveQueries = () => {
    if (activeCategory === 'all') return allQueries;
    return categories[activeCategory] || [];
  };

  return (
    <aside className="w-72 bg-white shadow-sm border-r border-gray-200 hidden md:block">
      <div className="px-6 pt-6 pb-4">
        <h2 className="text-lg font-bold text-gray-800 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Sample Queries
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Try these queries to explore your FAST TV analytics
        </p>
      </div>
      
      {/* Category tabs */}
      <div className="border-b border-gray-200 px-3">
        <nav className="flex space-x-4 overflow-x-auto pb-3 px-1">
          <button
            onClick={() => setActiveCategory('all')}
            className={`px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap ${
              activeCategory === 'all'
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setActiveCategory('viewership')}
            className={`px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap ${
              activeCategory === 'viewership'
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Viewership
          </button>
          <button
            onClick={() => setActiveCategory('demographics')}
            className={`px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap ${
              activeCategory === 'demographics'
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Demographics
          </button>
          <button
            onClick={() => setActiveCategory('qoe')}
            className={`px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap ${
              activeCategory === 'qoe'
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            QoE
          </button>
          <button
            onClick={() => setActiveCategory('conversion')}
            className={`px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap ${
              activeCategory === 'conversion'
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Conversion
          </button>
        </nav>
      </div>
      
      {/* Query list */}
      <div className="mt-4 px-4">
        <ul className="space-y-2 max-h-[calc(100vh-220px)] overflow-y-auto pb-4">
          {getActiveQueries().map((query, index) => (
            <li key={index}>
              <button
                onClick={() => onSampleQueryClick(query)}
                className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-50 transition-colors flex items-start"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-gray-700">{query}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      
      {/* Help section */}
      <div className="bg-indigo-50 p-4 m-4 rounded-lg">
        <h3 className="text-sm font-medium text-indigo-800">Need Help?</h3>
        <p className="text-xs text-indigo-600 mt-1">
          You can ask questions about viewership, demographics, QoE metrics, conversion rates, and content performance.
        </p>
        <div className="mt-3">
          <a href="#help" className="text-xs font-medium text-indigo-700 hover:text-indigo-900 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            View Documentation
          </a>
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
